import React, { ReactNode } from 'react';

import { Fade } from '@mui/material';

import { AppError } from '@vk-hr-tek/core/error';
import { Box } from '@vk-hr-tek/ui/Box';
import { HTTP_INTERNAL_SERVER_ERROR_STATUS } from '@vk-hr-tek/core/http/codes';

import { PageError } from './PageError';
import { PageHeader } from './PageHeader';
import { PageLoading } from './PageLoading';
import { PageLoadingText } from './PageLoadingText';

export type PageStatus =
  | 'idle'
  | 'loading'
  | 'loading-text'
  | 'complete'
  | 'failed'
  | 'aborted';

interface PageProps {
  actions?: React.ReactNode | React.ReactNode[];
  additionalActions?: React.ReactNode | React.ReactNode[];
  children: React.ReactNode | React.ReactNode[];
  error: AppError | null;
  link?: ReactNode;
  loadingText?: ReactNode;
  hideLoader?: boolean;
  showTitleMobile?: boolean;
  status: PageStatus;
  title: ReactNode;
  actionsPosition?: 'default' | 'absolute';
  asWidget?: boolean;
}

export const Page = ({
  actions = null,
  additionalActions = null,
  children,
  error,
  link,
  loadingText,
  hideLoader = false,
  showTitleMobile = false,
  status,
  title: titleNode,
  actionsPosition = 'default',
  asWidget = false,
}: PageProps) => {
  const title = (
    <PageHeader
      actions={actions}
      additionalActions={additionalActions}
      link={link}
      showTitleMobile={showTitleMobile}
      title={titleNode}
      actionsPosition={actionsPosition}
      asWidget={asWidget}
    />
  );

  if (status === 'idle') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{title}</>;
  }

  if (status === 'loading') {
    return (
      <>
        {title}
        <Fade timeout={1000} in>
          <div>
            <PageLoading />
          </div>
        </Fade>
      </>
    );
  }

  if (status === 'loading-text' || status === 'aborted') {
    return (
      <>
        {title}
        <Fade timeout={1000} in>
          <PageLoadingText text={loadingText} hideLoader={hideLoader} />
        </Fade>
      </>
    );
  }

  if (status === 'failed') {
    return (
      <>
        {title}
        <Fade timeout={1000} in>
          <div>
            <PageError
              status={error ? error.status : HTTP_INTERNAL_SERVER_ERROR_STATUS}
              traceId={error?.traceId}
            />
          </div>
        </Fade>
      </>
    );
  }

  return (
    <>
      {title}
      <Fade timeout={1000} in>
        <Box>{children}</Box>
      </Fade>
    </>
  );
};
